import React, { useCallback, useState } from 'react';

// Promise-returning replacement for window.confirm(). Native confirm()
// is unreliable in embedded browser contexts (some Electron builds, VS Code
// webviews, headless test harnesses) where it auto-resolves without showing
// the dialog at all — so a "Delete this layout?" guard silently fires the
// delete. This hook renders an in-app modal that always works.
//
// Usage:
//   const { confirm, dialog } = useConfirm();
//   const ok = await confirm({ message: '…', confirmLabel: 'Delete', danger: true });
//   if (!ok) return;
//   …
//   return (<>{dialog}<RestOfYourComponent /></>);

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:   { background: 'var(--bg-secondary)', padding: '20px', borderRadius: '8px', width: '420px', maxWidth: '95vw', border: '1px solid var(--border-color)', color: 'var(--text-primary)' },
  title:   { margin: 0, fontSize: '15px', fontWeight: 600 },
  body:    { fontSize: '13px', color: 'var(--text-secondary)', margin: '10px 0 16px', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  row:     { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  btn:     { background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontSize: '13px' },
  btnConfirm: (danger) => ({
    background: danger ? '#ef4444' : 'var(--accent-color)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '7px 14px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
  }),
};

export default function useConfirm() {
  // {message, title, confirmLabel, cancelLabel, danger, resolve} | null
  const [pending, setPending] = useState(null);

  const confirm = useCallback((opts) => {
    const o = typeof opts === 'string' ? { message: opts } : (opts || {});
    return new Promise((resolve) => {
      setPending({
        title: o.title || 'Confirm',
        message: o.message || 'Are you sure?',
        confirmLabel: o.confirmLabel || 'Confirm',
        cancelLabel: o.cancelLabel || 'Cancel',
        danger: !!o.danger,
        resolve,
      });
    });
  }, []);

  const finish = useCallback((value) => {
    setPending((cur) => {
      if (cur && typeof cur.resolve === 'function') cur.resolve(value);
      return null;
    });
  }, []);

  const dialog = pending ? (
    <div style={styles.overlay} onClick={() => finish(false)}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
        <h3 style={styles.title}>{pending.title}</h3>
        <p style={styles.body}>{pending.message}</p>
        <div style={styles.row}>
          <button onClick={() => finish(false)} style={styles.btn}>{pending.cancelLabel}</button>
          <button onClick={() => finish(true)} style={styles.btnConfirm(pending.danger)} autoFocus>
            {pending.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { confirm, dialog };
}
